/**
 * Shaping raw `attendance_records` rows into something an admin can read and export.
 *
 * Everything here is pure so the CSV — the artefact that leaves the building and ends up in
 * a spreadsheet, an email, or a conversation with a parent — is testable without a database
 * or a browser.
 *
 * The one idea worth holding on to: **a row's class is not its program.** A Chinese or Math
 * record carries the child's *after-school* `program_id`, because the after-school seat is
 * what puts them in the room. Reading the program name alone would label all three of a
 * child's daily rows "Regular G1-G5 Afterschool" and make one child's Tuesday look like
 * three attendances. `virtual_class_key` is what tells them apart, and it is the first thing
 * every function here looks at.
 */

import { parseVirtualClassKey } from '../staff-app/virtualClasses';
import { REAL_SESSION_KEY, type AttendanceStatus } from '../types/attendance';
import { formatDateDisplay, formatTimeInBusinessTZ } from './date';

/** One attendance record, flattened with the joined names it is read alongside. */
export interface AttendanceHistoryRow {
  id: string;
  studentId: string;
  /** "First Last" — how a teacher reads a name off a roster row. */
  studentName: string;
  /** "Last, First" — how a roster is read and how this table sorts. */
  studentSortName: string;
  studentGrade: string | null;
  studentSchool: string | null;
  sessionDate: string;
  status: AttendanceStatus;
  /** `''` for a real session; otherwise `'chinese:3'` and friends. */
  virtualClassKey: string;
  programName: string | null;
  programType: string | null;
  checkedInAt: string | null;
  checkedInBy: string | null;
  checkedInByName: string | null;
  pickedUpAt: string | null;
  pickedUpByName: string | null;
  pickupIsAuthorized: boolean | null;
  pickupRecordedByName: string | null;
  absenceReason: string | null;
}

/**
 * What class this row belongs to, as a person would name it.
 *
 * A virtual row is its class — "Chinese G3" — and never the after-school program whose id
 * it borrows. A real row is its program. An unparseable key falls back to the raw value
 * rather than the program name, because silently relabelling an unknown key as after-school
 * is exactly the miscount this function exists to prevent.
 */
export function attendanceClassLabel(
  virtualClassKey: string,
  programName: string | null,
): string {
  if (virtualClassKey && virtualClassKey !== REAL_SESSION_KEY) {
    return parseVirtualClassKey(virtualClassKey)?.label ?? virtualClassKey;
  }
  return programName ?? 'Unknown program';
}

/**
 * How a status reads in a table cell.
 *
 * `not_arrived` is the value every row is seeded with, so on a day already past it means
 * nobody recorded anything either way — an unknown, not an absence. Calling it "Absent"
 * here would invent attendance data, and this table is the thing someone would cite.
 */
export const HISTORY_STATUS_LABEL: Record<AttendanceStatus, string> = {
  checked_in: 'Checked in',
  picked_up: 'Checked out',
  absent: 'Absent',
  not_arrived: 'No record',
};

/** Statuses offered in the filter, in the order they read. */
export const HISTORY_STATUSES: readonly AttendanceStatus[] = [
  'picked_up',
  'checked_in',
  'absent',
  'not_arrived',
];

/** One session date's records, with the tally a teacher scans a card header for. */
export interface AttendanceDay {
  /** `YYYY-MM-DD`, the DATE column as stored. */
  date: string;
  rows: AttendanceHistoryRow[];
  tallies: Record<AttendanceStatus, number>;
  total: number;
}

/**
 * History rows folded into one card per session date.
 *
 * The admin reads this archive as a table because the question there spans children and
 * weeks at once — "which handovers went to someone off the list last term". A teacher's
 * question is the other shape: one class, one day at a time, "was she here on Tuesday". So
 * the phone gets a card per day whose header carries the tally, and the names stay folded
 * away until asked for.
 *
 * Insertion order is preserved rather than re-sorted, because the query already ordered
 * these — newest day first, then as a register reads. Re-sorting here would be a second
 * opinion about an order the database already has right, and would silently disagree with
 * it the day one of them changes.
 */
export function groupAttendanceByDay(rows: readonly AttendanceHistoryRow[]): AttendanceDay[] {
  const days = new Map<string, AttendanceDay>();

  for (const row of rows) {
    let day = days.get(row.sessionDate);
    if (!day) {
      day = {
        date: row.sessionDate,
        rows: [],
        tallies: { not_arrived: 0, checked_in: 0, picked_up: 0, absent: 0 },
        total: 0,
      };
      days.set(row.sessionDate, day);
    }
    day.rows.push(row);
    day.tallies[row.status] += 1;
    day.total += 1;
  }

  return [...days.values()];
}

/** A timestamptz as a wall-clock time in the business timezone, or an em dash. */
function clock(iso: string | null): string {
  return iso ? formatTimeInBusinessTZ(iso, { hour12: true }) : '';
}

/**
 * Who checked the child in, phrased as the teacher portal phrases it.
 *
 * A parent check-in records no name — the parent surface was removed and nothing writes one
 * — so the role stands in for a person there.
 */
export function checkedInByLabel(row: AttendanceHistoryRow): string {
  if (!row.checkedInAt) return '';
  if (row.checkedInBy === 'parent') return 'parent';
  return row.checkedInByName || row.checkedInBy || '';
}

function escapeCsv(value: string): string {
  return /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/**
 * Columns are one fact each, never a rendered sentence.
 *
 * The table can afford to fold "checked out to X at Y, released by Z" into one cell; a
 * spreadsheet cannot, because the first thing anyone does with this file is sort or pivot
 * it. `Authorized` is its own yes/no column for the same reason: "was this handover to
 * someone off the list" is the question most likely to be asked of an export, and it should
 * be a filter in Excel rather than a search for a warning glyph.
 */
export const HISTORY_CSV_HEADER = [
  'Date',
  'Student',
  'Grade',
  'School',
  'Class',
  'Program',
  'Program type',
  'Status',
  'Checked in at',
  'Checked in by',
  'Checked out at',
  'Collected by',
  'Authorized',
  'Released by',
  'Absence reason',
] as const;

export function attendanceHistoryCsvRow(row: AttendanceHistoryRow): string[] {
  return [
    formatDateDisplay(row.sessionDate),
    row.studentSortName,
    row.studentGrade ?? '',
    row.studentSchool ?? '',
    attendanceClassLabel(row.virtualClassKey, row.programName),
    row.programName ?? '',
    row.programType ?? '',
    HISTORY_STATUS_LABEL[row.status],
    clock(row.checkedInAt),
    checkedInByLabel(row),
    clock(row.pickedUpAt),
    row.pickedUpByName ?? '',
    // Blank rather than "Yes" when nobody was collected: a child still on site has not had
    // an unauthorized handover, and defaulting the column to Yes would say they had.
    row.status === 'picked_up' ? (row.pickupIsAuthorized === false ? 'No' : 'Yes') : '',
    row.pickupRecordedByName ?? '',
    row.absenceReason ?? '',
  ];
}

export function buildAttendanceHistoryCsv(rows: readonly AttendanceHistoryRow[]): string {
  return [HISTORY_CSV_HEADER as readonly string[], ...rows.map(attendanceHistoryCsvRow)]
    .map(cells => cells.map(cell => escapeCsv(String(cell))).join(','))
    .join('\n');
}

/** `attendance-history-2026-06-08-to-2026-08-14.csv` — the range is the file's identity. */
export function attendanceHistoryFilename(from: string, to: string): string {
  return `attendance-history-${from}-to-${to}.csv`;
}
